import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { DatePicker } from 'antd';
import DateFilter from '../../datePicker/DateFilter';
import { getDateRangeFromConfig } from '../../analytics/analytics.helpers';
import OnTraccrRadioGroup from '../../common/inputs/OnTraccrRadioGroup';

const { RangePicker } = DatePicker;

const style = {
  width: 250,
};

export default function ReportDateSelector({
  onTypeChange,
  selectedType,
  range,
  onChange,
  onRangeChange,
  hideCustom = false,
  allowClear = true,
  renderDropdownInBody = false,
  allowFutureDays = false,
  hideAllTime = false,
}) {
  const [datePreset, setDatePreset] = useState(undefined);
  const [open, setOpen] = useState(false);

  const onPresetClicked = useCallback((newDatePreset) => {
    setDatePreset(newDatePreset);
    const presetRange = getDateRangeFromConfig({ dateRange: range, datePreset: newDatePreset });
    onRangeChange(presetRange);
    onTypeChange('custom');
    setOpen(false);
  }, [datePreset, onTypeChange, range, onRangeChange]);

  const group = [{
    label: 'Pay Period', value: 'payPeriod',
  }, {
    label: 'Week', value: 'week',
  }];
  if (!hideCustom) {
    group.push({
      label: 'Custom', value: 'custom',
    });
  }

  const getPopupContainer = useCallback((node) => (
    node && !renderDropdownInBody ? node.parentNode : document.body
  ), [renderDropdownInBody]);

  const renderExtraFooter = useCallback(() => (
    <>
      <OnTraccrRadioGroup
        options={group}
        onChange={(type) => {
          onTypeChange(type);
          if (type !== 'custom') {
            setDatePreset(undefined);
          }
        }}
        value={selectedType}
      />
      <DateFilter
        datePreset={datePreset}
        handlePreset={onPresetClicked}
        hideAllTime={hideAllTime}
      />
    </>
  ), [group, datePreset, onPresetClicked, onTypeChange, hideAllTime]);

  const disabledDate = useCallback((current) => (
    current > moment().endOf('day') && !allowFutureDays
  ), [allowFutureDays]);

  const formatRange = useCallback(([t1, t2]) => {
    if (!t1 || !t2) return null;
    const sameMonth = t1.format('MMM') === t2.format('MMM');
    const start = t1.format('MMM D');
    const end = sameMonth ? t2.format('D, YY') : t2.format('MMM D, YY');
    return `${start} - ${end}`;
  }, []);

  switch (selectedType) {
    case 'payPeriod':
      return (
        <DatePicker
          style={style}
          format={`[Pay Period: ${formatRange(range)}]`}
          disabledDate={disabledDate}
          renderExtraFooter={renderExtraFooter}
          value={range[0]}
          open={open}
          onOpenChange={setOpen}
          onChange={onChange}
          getPopupContainer={getPopupContainer}
          allowClear={allowClear}
        />
      );
    case 'week':
      return (
        <DatePicker
          style={style}
          format="[Week of] MMM Do YY"
          disabledDate={disabledDate}
          renderExtraFooter={renderExtraFooter}
          picker="week"
          value={range[0]}
          open={open}
          onOpenChange={setOpen}
          onChange={onChange}
          getPopupContainer={getPopupContainer}
          allowClear={allowClear}
        />
      );
    default:
      return (
        <RangePicker
          style={style}
          format="MMM Do YY"
          disabledDate={disabledDate}
          renderExtraFooter={renderExtraFooter}
          value={range}
          open={open}
          onOpenChange={setOpen}
          onChange={onRangeChange}
          getPopupContainer={getPopupContainer}
          allowClear={allowClear}
        />
      );
  }
}

ReportDateSelector.propTypes = {
  onTypeChange: PropTypes.func.isRequired,
  selectedType: PropTypes.string.isRequired,
  range: PropTypes.arrayOf(PropTypes.instanceOf(moment)).isRequired,
  onChange: PropTypes.func.isRequired,
  onRangeChange: PropTypes.func.isRequired,
  hideCustom: PropTypes.bool,
  allowClear: PropTypes.bool,
  renderDropdownInBody: PropTypes.bool,
  allowFutureDays: PropTypes.bool,
  hideAllTime: PropTypes.bool,
};

ReportDateSelector.defaultProps = {
  hideCustom: false,
  allowClear: true,
  renderDropdownInBody: false,
  allowFutureDays: false,
  hideAllTime: false,
};
